import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFetchRequest, postFetchRequest, patchFetchRequest, deleteFetchRequest, GET_ORDERS, MAKE_CUSTOMER_REQUEST, GET_PRODUCT_SEARCH, ADMIN_CREATE_ORDER, SUPPLIER_ORDERS_URL } from '../store/requests';
import { makeUrl } from '../constant';
import AutobaseRunPanel from './AutobaseRunPanel';
import ClientsTableOverlayScroll from './ClientsTableOverlayScroll';
import OrderProductHintsLayer from './OrderProductHintsLayer';

const LIST_OF_STATUSES = {
    CREATED: 'Создан',
    PLACED: 'Размещен',
    PAID: 'Оплачено',
    DONE: 'Выполнен',
    CANCELLED: 'Отменен',
    AWAITING_PAYMENT: 'Ожидает оплаты',
    DELETED: 'Удален',
    SHIPPED: 'Отгружен',
    PARTIALLY_PLACED: 'Частично размещен',
    SUPPLIER_PLACED: 'Размещен',
};

const DEFAULT_VAT_PERCENT = 22;
const DEMO_PRODUCT = {
    productId: 'demo-product',
    productName: 'Демо товар',
    ourStock: '40 шт.',
    reserve: '40',
    deliveryTerm: '25 раб. дней',
    deliveryDate: '2026-04-01',
    supplierOrders: 'HZ2025120115-1',
};
const DEMO_SUPPLIER_PRODUCT = {
    productId: 'demo-product-supplier',
    productName: 'Демо товар (поставщик)',
    supplierStock: '34 шт.',
    reserve: '34',
    deliveryTerm: '19 раб. дней',
    deliveryDate: '2026-04-01',
};

const formatDate = (dateString, keepDash = false) => {
    if (!dateString) return keepDash ? '-' : '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
};

const formatDateInput = (dateValue) => {
    if (!dateValue) return '';
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
        return dateValue.slice(0, 10);
    }
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getDefaultValidUntil = (order) => {
    const saved = order?.validUntil || order?.valid_until;
    if (saved) {
        return formatDateInput(saved);
    }
    const baseDate = order?.date_creation || order?.dateCreation || order?.datePlaced || order?.date_placed || new Date();
    const computed = addDays(baseDate, 2);
    return computed ? formatDateInput(computed) : '';
};

const getHintPartNumber = (hint) =>
    (hint.productPartNumber || hint.partNumber || hint.article || hint.code || '').trim();

const getHintManufacturer = (hint) =>
    (
        hint.productManufacturerName ||
        hint.manufacturerName ||
        hint.mfr ||
        (hint.manufacturer && hint.manufacturer.name) ||
        ''
    ).trim();

const formatProductFieldValue = (partNumber, manufacturerName) => {
    const part = (partNumber || '').trim();
    const manufacturer = (manufacturerName || '').trim();
    if (part && manufacturer) {
        return `${part} (${manufacturer})`;
    }
    return part;
};

const compareCategoryNames = (left, right) =>
    String(left?.categoryName || '').localeCompare(String(right?.categoryName || ''), 'ru', { sensitivity: 'base' });

const flattenCategories = (categories) => (
    [...(Array.isArray(categories) ? categories : [])]
        .sort(compareCategoryNames)
        .flatMap((parent) => {
            const parentCount = Number(parent.productsNumber);
            const parentSafeCount = Number.isFinite(parentCount) ? parentCount : 0;
            const parentOption = {
                id: parent.categoryId,
                name: parent.categoryName,
                singular: parent.categoryNameSingular || '',
                label: `${parent.categoryName} (${parentSafeCount})`,
            };
            const children = [...(parent.subCategories || [])]
                .sort(compareCategoryNames)
                .map((sub) => {
                    const count = Number(sub.productsNumber);
                    const safeCount = Number.isFinite(count) ? count : 0;
                    return {
                        id: sub.categoryId,
                        name: sub.categoryName,
                        singular: sub.categoryNameSingular || '',
                        label: `${parent.categoryName} / ${sub.categoryName} (${safeCount})`,
                    };
                });
            return [parentOption, ...children];
        })
);

const categoryDisplayName = (category) => (category.singular || '').trim() || category.name || '';

const collectProductCategories = (products) => {
    const byProduct = new Map();
    (Array.isArray(products) ? products : []).forEach((product) => {
        if (!product.categoryAssigned || !product.categoryId || !product.productId) {
            return;
        }
        if (String(product.productId).startsWith('draft-')) {
            return;
        }
        byProduct.set(product.productId, product.categoryId);
    });
    return Array.from(byProduct, ([productId, categoryId]) => ({ productId, categoryId }));
};

const mapCatalogItemToHint = (item) => {
    const manufacturer = item.manufacturer || {};
    return {
        productId: item.id || item.productId || null,
        productPartNumber: (item.partNumber || item.productPartNumber || '').trim(),
        productManufacturerId: manufacturer.id || item.manufacturerId || item.productManufacturerId || null,
        productManufacturerName: manufacturer.name || item.manufacturerName || item.productManufacturerName || '',
        categoryName: item.categoryName || item.productCategoryName || '',
    };
};

const extractPartNumberHints = (response) => {
    const list = Array.isArray(response) ? response : [];
    return list.filter((hint) => getHintPartNumber(hint)).slice(0, 10);
};

const extractSearchHints = (response) => {
    const items = Array.isArray(response && response.items) ? response.items : [];
    return items.map(mapCatalogItemToHint).filter((hint) => getHintPartNumber(hint)).slice(0, 5);
};

const fetchOrderProductHints = (query, onSuccess, onError) => {
    const hintUrl = makeUrl([GET_PRODUCT_SEARCH, 'hint/', `?q=${encodeURIComponent(query)}`]);
    const searchUrl = makeUrl([GET_PRODUCT_SEARCH, `?q=${encodeURIComponent(query)}&offset=1&limit=5`]);
    const loadSearchFallback = () => {
        getFetchRequest(
            searchUrl,
            (searchResponse) => onSuccess(extractSearchHints(searchResponse)),
            onError
        );
    };

    getFetchRequest(
        hintUrl,
        (response) => {
            const partHints = extractPartNumberHints(response);
            if (partHints.length > 0) {
                onSuccess(partHints);
                return;
            }
            loadSearchFallback();
        },
        () => loadSearchFallback()
    );
};

const getDuplicateHintPartNumbers = (hints) => {
    const counts = {};
    (hints || []).forEach((hint) => {
        const partNumber = getHintPartNumber(hint).toLowerCase();
        if (!partNumber) {
            return;
        }
        counts[partNumber] = (counts[partNumber] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((partNumber) => counts[partNumber] > 1));
};

const formatOrderHintLabel = (hint, showManufacturer) => {
    const partNumber = getHintPartNumber(hint);
    const manufacturer = getHintManufacturer(hint);
    if (showManufacturer && manufacturer) {
        return `${partNumber} (${manufacturer})`;
    }
    return partNumber;
};

const addDays = (dateString, days) => {
    try {
        const base = new Date(dateString);
        if (Number.isNaN(base.getTime())) return null;
        const copy = new Date(base);
        copy.setDate(copy.getDate() + days);
        return copy;
    } catch {
        return null;
    }
};

const formatCurrency = (value, currency) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '';
    }
    const number = Number(value);
    return `${number.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '';
    }
    return `${Number(value)}%`;
};

const formatPiecesWithDefault = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }
    const text = String(value).trim();
    if (text === '') return '-';
    if (/шт\.?$/i.test(text)) return text;
    return `${text} шт.`;
};

const parseNumeric = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const text = String(value).replace(',', '.');
    const match = text.match(/-?\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : 0;
};

// Пустой срок поставки — это не «0 дней». В числовой колонке храним -1.
const getOrderRowKey = (product) => {
    if (product?.isDraft) {
        return `draft-${product.rowId || product.productId}`;
    }
    return `saved-${product.orderProductId || product.id}`;
};

const supplierOptionKey = (product) => `${getOrderRowKey(product)}-${product?.productId || ''}`;

const parseDeliveryTermDays = (value) => {
    if (value === null || value === undefined) return -1;
    const text = String(value).trim();
    if (!text) return -1;
    const match = text.replace(',', '.').match(/-?\d+(\.\d+)?/);
    if (!match) return -1;
    const parsed = parseFloat(match[0]);
    return Number.isNaN(parsed) ? -1 : parsed;
};

const calculateOrderSummary = (order) => {
    let totalWithVat = 0;
    let vatSum = 0;
    let hasVatInfo = false;

    if (order?.products?.length) {
        order.products.forEach((product) => {
            // Используем parseNumeric, чтобы поддержать строки вида "5 шт." и "12,5"
            const qty = parseNumeric(product.productCount ?? product.quantity ?? product.count ?? 0);
            const price = parseNumeric(product.productPrice ?? product.price ?? 0);
            const lineTotal = qty * price;
            if (!lineTotal || Number.isNaN(lineTotal)) {
                return;
            }
            totalWithVat += lineTotal;

            const rawVat = product.vatPercent ?? product.vat ?? product.nds ?? DEFAULT_VAT_PERCENT;
            const vatPercent = rawVat !== undefined && rawVat !== null ? Number(String(rawVat).replace('%', '')) : null;
            if (!Number.isNaN(vatPercent) && vatPercent > 0) {
                hasVatInfo = true;
                // Предполагаем, что цена с НДС; выделяем НДС из суммы
                vatSum += lineTotal * vatPercent / (100 + vatPercent);
            }
        });
    }

    if (!totalWithVat) {
        totalWithVat = Number(order?.totalCost ?? 0);
    }

    // Если не было явной ставки НДС, не показываем значение
    if (!hasVatInfo) {
        vatSum = null;
    }

    return {
        vat: vatSum,
        total: totalWithVat,
    };
};

const OrdersTable = ({ ordersList, onOrderClick, onApproveInvoice, approveLoading, pendingApprove, onCreateOrder, onDeleteOrder }) => {
    const renderRow = (order, index) => {
        const orderNumber = order.order_number ?? order.orderNumber ?? '';
        const date = formatDate(order.date_creation ?? order.dateCreation);
        const customer = order.customerName ?? order.customer_id ?? '';
        const total = formatCurrency(order.totalCost, order.currency);
        const orderCurrency = order.currency || 'RUB';
        const hasInvoice = order.has_invoice ?? order.hasInvoice;
        const isPlaced = order.status === 'PLACED';
        const needsInvoiceApproval = isPlaced && !hasInvoice;
        const pending = pendingApprove && pendingApprove[order.id] === true;
        const hasRecordedPayments = order.paidAmount !== null && order.paidAmount !== undefined;
        const paidAmount = needsInvoiceApproval
            ? 'Одобрить счет'
            : (hasRecordedPayments
                ? order.paidAmount
                : (order.date_paid || order.datePaid ? order.totalCost : null));
        const paidPercent = needsInvoiceApproval
            ? null
            : (hasRecordedPayments
                ? (order.paymentPercent ?? null)
                : (order.paymentPercent ?? (order.date_paid || order.datePaid ? 100 : null)));
        const datePaid = formatDate(
            hasRecordedPayments
                ? (order.paymentDate ?? order.date_paid ?? order.datePaid)
                : (order.date_paid ?? order.datePaid)
        );
        const status = LIST_OF_STATUSES[order.status] || order.status || '';
        const shippingPercent = formatPercent(order.shippingPercent);
        const shippingDeadline = formatDate(order.latestDeliveryDate ?? order.latest_delivery_date);
        const fulfillmentDays = order.fulfillmentBusinessDays ?? order.fulfillment_business_days;
        const hasFulfillmentDays = fulfillmentDays !== null && fulfillmentDays !== undefined && fulfillmentDays !== '';
        const fulfillmentUrgent = hasFulfillmentDays && Number(fulfillmentDays) <= 3;
        const doneDate = hasFulfillmentDays ? `${fulfillmentDays} раб. дн.` : '';

        return (
            <div
                key={order.id || index}
                className="clients-table__row"
                onClick={() => onOrderClick && onOrderClick(order)}
            >
                <div className="clients-table__cell clients-table__cell--id">{orderNumber}</div>
                <div className="clients-table__cell">{date}</div>
                <div className="clients-table__cell">{customer}</div>
                <div className="clients-table__cell">{total}</div>
                <div className="clients-table__cell">{orderCurrency}</div>
                <div className="clients-table__cell clients-table__cell--pay">
                    {needsInvoiceApproval
                        ? <span>Одобрить счет</span>
                        : (paidAmount !== null && paidAmount !== undefined ? formatCurrency(paidAmount, order.currency) : '')}
                </div>
                <div className="clients-table__cell">
                    {needsInvoiceApproval ? (
                        <label className="order-toggle" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                className="order-toggle__input"
                                checked={pending || false}
                                disabled={approveLoading[order.id] === true}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    if (onApproveInvoice) {
                                        onApproveInvoice(order);
                                    }
                                }}
                            />
                            <span className="order-toggle__slider"></span>
                        </label>
                    ) : (
                        formatPercent(paidPercent)
                    )}
                </div>
                <div className="clients-table__cell">{datePaid}</div>
                <div className="clients-table__cell">{status}</div>
                <div className="clients-table__cell">{shippingPercent}</div>
                <div className="clients-table__cell">{shippingDeadline}</div>
                <div
                    className="clients-table__cell"
                    style={fulfillmentUrgent ? { color: 'red' } : undefined}
                >
                    {doneDate}
                </div>
                <button
                    type="button"
                    className="order-row-remove-btn"
                    onClick={(e) => onDeleteOrder && onDeleteOrder(order, e)}
                    aria-label="Удалить заказ"
                >
                    <span className="order-row-remove-btn__icon">×</span>
                </button>
            </div>
        );
    };

    return (
        <article className="users">
            <div className="clients-toolbar">
                <button
                    type="button"
                    className="clients-toolbar__create"
                    onClick={onCreateOrder}
                >
                    Создать заказ +
                </button>
                <div className="clients-toolbar__search">
                    <input type="text" placeholder="Поиск ...." />
                    <svg className="clients-toolbar__search-icon" width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.7938 14.6615C14.6355 14.6615 17.7498 11.5473 17.7498 7.70574C17.7498 3.86419 14.6355 0.75 10.7938 0.75C6.95218 0.75 3.83789 3.86419 3.83789 7.70574C3.83789 11.5473 6.95218 14.6615 10.7938 14.6615Z" stroke="#1C1C1C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.87594 12.6245L0.75 17.7503" stroke="#1C1C1C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
            </div>
            <div className="clients-table clients-table--orders">
                <div className="clients-table__header">
                    <div className="clients-table__row">
                        <div className="clients-table__cell clients-table__cell--id">Номер</div>
                        <div className="clients-table__cell">Дата</div>
                        <div className="clients-table__cell">Клиент</div>
                        <div className="clients-table__cell">Сумма</div>
                        <div className="clients-table__cell">Валюта</div>
                        <div className="clients-table__cell">Оплачено</div>
                        <div className="clients-table__cell">% оплаты</div>
                        <div className="clients-table__cell">Дата оплаты</div>
                        <div className="clients-table__cell">Состояние заказа</div>
                        <div className="clients-table__cell">% Отгрузки</div>
                        <div className="clients-table__cell">Крайняя дата отгрузки</div>
                        <div className="clients-table__cell">На выполнение заказа</div>
                    </div>
                </div>
                <div className="clients-table__body">
                    {ordersList.length === 0 ? (
                        <div className="clients-table__row">
                            <div className="clients-table__cell" style={{ gridColumn: '1 / -1' }}>
                                Заказы не найдены
                            </div>
                        </div>
                    ) : (
                        ordersList.map(renderRow)
                    )}
                </div>
            </div>
        </article>
    );
};

// Компонент для детального вида заказа
const OrderDetail = ({ order, onBack, onSendInvoice, onPrintInvoice, isNew = false, customers = [], onOrderCreated, onOrderUpdated, onValidUntilSaved, onInvoiceFormSaved }) => {
    const navigate = useNavigate();
    const [isInvoiceToggleOn, setIsInvoiceToggleOn] = useState(false);
    const [isInvoiceToggleLoading, setIsInvoiceToggleLoading] = useState(false);
    const [shipActiveState, setShipActiveState] = useState({});
    const [supplierOrderSelection, setSupplierOrderSelection] = useState({});
    const [supplierOptionsByProduct, setSupplierOptionsByProduct] = useState({});
    const [supplierOptionsLoading, setSupplierOptionsLoading] = useState({});
    const [addedProducts, setAddedProducts] = useState([]);
    const [rowOrder, setRowOrder] = useState([]);
    const [selectedRowKey, setSelectedRowKey] = useState(null);
    const [orderDirty, setOrderDirty] = useState(false);
    const [hints, setHints] = useState([]);
    const [hintsFetched, setHintsFetched] = useState(false);
    const [activeDraftProductId, setActiveDraftProductId] = useState(null);
    const [searchState, setSearchState] = useState({ productId: null, query: '' });
    const [catalogAddModal, setCatalogAddModal] = useState({ open: false, partNumber: '' });
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [categoriesList, setCategoriesList] = useState([]);
    const [validUntil, setValidUntil] = useState(() => getDefaultValidUntil(order));
    const [currency, setCurrency] = useState(order?.currency || 'RUB');
    const [invoiceForm, setInvoiceForm] = useState(order?.invoiceForm || order?.invoice_form || 'offer');
    const validUntilRequestRef = useRef(0);
    const productAnchorRef = useRef(null);
    const tableScrollRef = useRef(null);
    const scrollAfterAddRef = useRef(false);

    const handleValidUntilChange = (value) => {
        setValidUntil(value);
        if (isNew || !order?.id || order.id === 'new') {
            return;
        }
        const previous = formatDateInput(order.validUntil || order.valid_until);
        if ((value || '') === (previous || '')) {
            return;
        }
        const requestId = validUntilRequestRef.current + 1;
        validUntilRequestRef.current = requestId;
        patchFetchRequest(
            makeUrl([GET_ORDERS, order.id, '/']),
            { validUntil: value || null },
            (data) => {
                if (requestId !== validUntilRequestRef.current) {
                    return;
                }
                if (onValidUntilSaved) {
                    onValidUntilSaved(order.id, data?.validUntil ?? value ?? null);
                }
            },
            () => {
                if (requestId !== validUntilRequestRef.current) {
                    return;
                }
                alert('Не удалось сохранить дату «Действует до».');
            }
        );
    };

    useEffect(() => {
        getFetchRequest(
            makeUrl(['/api/v1/category/']),
            (response) => setCategoriesList(Array.isArray(response) ? response : []),
            () => setCategoriesList([])
        );
    }, []);

    useEffect(() => {
        if (isNew) {
            return;
        }
        const saved = order?.validUntil || order?.valid_until;
        if (saved) {
            setValidUntil(formatDateInput(saved));
        }
    }, [isNew, order?.id, order?.validUntil, order?.valid_until]);

    useEffect(() => {
        setInvoiceForm(order?.invoiceForm || order?.invoice_form || 'offer');
    }, [order?.id, order?.invoiceForm, order?.invoice_form]);

    const handleInvoiceFormChange = (value) => {
        setInvoiceForm(value);
        if (isNew || !order?.id || order.id === 'new') {
            return;
        }
        patchFetchRequest(
            makeUrl([GET_ORDERS, order.id, '/']),
            { invoiceForm: value },
            (data) => {
                if (onInvoiceFormSaved) {
                    onInvoiceFormSaved(order.id, data?.invoiceForm || value);
                }
            },
            () => {
                alert('Не удалось сохранить бланк.');
                setInvoiceForm(order?.invoiceForm || order?.invoice_form || 'offer');
            }
        );
    };

    const productsWithDemo = useMemo(() => {
        const base = Array.isArray(order?.products) ? order.products : [];
        const byKey = new Map();
        base.forEach((product) => byKey.set(getOrderRowKey(product), product));
        addedProducts.forEach((product) => byKey.set(getOrderRowKey(product), product));
        const ordered = rowOrder.map((key) => byKey.get(key)).filter(Boolean);
        if (ordered.length) {
            return ordered;
        }
        return [...base, ...addedProducts];
    }, [order?.products, addedProducts, rowOrder]);

    const hasPayments = Array.isArray(order?.payments) && order.payments.length > 0;
    const canAddProducts = isNew || !hasPayments;
    const canReorder = !hasPayments;

    useEffect(() => {
        // Сбрасываем/инициализируем состояние при смене заказа
        setIsInvoiceToggleOn(Boolean(order?.has_invoice));
        setIsInvoiceToggleLoading(false);
        if (isNew) {
            setSelectedCustomerId(order?.customer_id || '');
            setSelectedUserId(null);
        }
        setAddedProducts([]);
        setRowOrder([]);
        setSelectedRowKey(null);
        setOrderDirty(false);
        const initialSupplierOrders = {};
        const initialShipState = {};
        if (productsWithDemo.length) {
            productsWithDemo.forEach((product) => {
                const productKey = supplierOptionKey(product);
                initialSupplierOrders[productKey] = product.supplierOrders ?? product.vendorOrders ?? '';
                initialShipState[productKey] = false;
            });
        }
        setSupplierOrderSelection(initialSupplierOrders);
        setShipActiveState(initialShipState);
    }, [order?.id, isNew, order?.customer_id]);

    useEffect(() => {
        // Для черновиков строк подгружаем заказы поставщика по товару,
        // чтобы можно было оценить, хватит ли поставщика.
        if (!productsWithDemo.length) return;

        productsWithDemo.forEach((product) => {
            if (!product.isDraft) {
                return;
            }
            const realProductId = product.productId;
            if (!realProductId || String(realProductId).startsWith('draft-')) {
                return;
            }
            const productKey = supplierOptionKey(product);

            if (supplierOptionsByProduct[productKey] || supplierOptionsLoading[productKey]) {
                return;
            }

            const url = makeUrl([SUPPLIER_ORDERS_URL, 'by-product/', realProductId, '/']);
            setSupplierOptionsLoading((prev) => ({ ...prev, [productKey]: true }));
            getFetchRequest(
                url,
                (response) => {
                    const list = Array.isArray(response) ? response : [];
                    setSupplierOptionsByProduct((prev) => ({ ...prev, [productKey]: list }));
                    setSupplierOptionsLoading((prev) => ({ ...prev, [productKey]: false }));
                },
                (error) => {
                    console.error('Ошибка загрузки заказов поставщика по товару:', error);
                    setSupplierOptionsLoading((prev) => ({ ...prev, [productKey]: false }));
                }
            );
        });
    }, [productsWithDemo, supplierOptionsByProduct, supplierOptionsLoading]);

    useEffect(() => {
        const base = Array.isArray(order?.products) ? order.products : [];
        const serverKeys = base.map(getOrderRowKey);
        const draftKeys = addedProducts.map(getOrderRowKey);
        setRowOrder((prev) => {
            if (!orderDirty && addedProducts.length === 0) {
                return serverKeys;
            }
            const alive = new Set([...serverKeys, ...draftKeys]);
            const kept = prev.filter((key) => alive.has(key));
            const keptSet = new Set(kept);
            const missingSaved = serverKeys.filter((key) => !keptSet.has(key));
            const missingDrafts = draftKeys.filter((key) => !keptSet.has(key));
            return [...kept, ...missingSaved, ...missingDrafts];
        });
    }, [order?.products, addedProducts, orderDirty]);

    useEffect(() => {
        if (selectedRowKey && !rowOrder.includes(selectedRowKey)) {
            setSelectedRowKey(null);
        }
    }, [rowOrder, selectedRowKey]);

    useEffect(() => {
        const handlePointerDown = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                setSelectedRowKey(null);
                return;
            }
            if (target.closest('[data-order-row]') || target.closest('[data-order-move-buttons]')) {
                return;
            }
            setSelectedRowKey(null);
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, []);

    useEffect(() => {
        if (!isNew) return;
        setAddedProducts((prev) => {
            if (prev && prev.length > 0) {
                return prev;
            }
            const newId = `draft-${Date.now()}-1`;
            return [{
                rowId: newId,
                productId: newId,
                productName: '',
                productCount: '',
                productPrice: '',
                deliveryTerm: '',
                isDraft: true,
            }];
        });
    }, [isNew, order?.id]);

    const handleAddProductRow = () => {
        if (!canAddProducts) return;
        const newId = `draft-${Date.now()}`;
        const rowKey = `draft-${newId}`;
        scrollAfterAddRef.current = true;
        setAddedProducts((prev) => [
            ...prev,
            {
                rowId: newId,
                productId: newId,
                productName: '',
                productCount: '',
                productPrice: '',
                isDraft: true,
            },
        ]);
        setRowOrder((prev) => (prev.includes(rowKey) ? prev : [...prev, rowKey]));
    };

    const moveSelectedRow = (direction) => {
        if (!canReorder || !selectedRowKey) {
            return;
        }
        const index = rowOrder.indexOf(selectedRowKey);
        const nextIndex = index + direction;
        if (index < 0 || nextIndex < 0 || nextIndex >= rowOrder.length) {
            return;
        }
        setRowOrder((prev) => {
            const currentIndex = prev.indexOf(selectedRowKey);
            const targetIndex = currentIndex + direction;
            if (currentIndex < 0 || targetIndex < 0 || targetIndex >= prev.length) {
                return prev;
            }
            const next = [...prev];
            const [moved] = next.splice(currentIndex, 1);
            next.splice(targetIndex, 0, moved);
            return next;
        });
        setOrderDirty(true);
    };

    const handleRemoveSavedProduct = (orderProductId, productName) => {
        if (!order?.id || !orderProductId) {
            return;
        }
        const label = productName || 'этот товар';
        if (!window.confirm(`Удалить «${label}» из заказа?`)) {
            return;
        }
        deleteFetchRequest(
            makeUrl([GET_ORDERS, order.id, '/order-product/', orderProductId, '/']),
            () => {
                if (onOrderUpdated) {
                    onOrderUpdated(order.id);
                }
            },
            () => {
                alert('Не удалось удалить товар из заказа.');
            }
        );
    };

    const handleRemoveDraftProduct = (productId) => {
        setAddedProducts((prev) => {
            if (isNew && (!prev || prev.length <= 1)) {
                return prev;
            }
            return (prev || []).filter((product) => product.productId !== productId);
        });
    };

    const handleDraftProductChange = (productId, field, value) => {
        setAddedProducts((prev) =>
            prev.map((product) => {
                if (product.productId !== productId) return product;

                let nextValue = value;

                if (field === 'productCount') {
                    const prevRaw = product.productCount ?? '';
                    const prevDigits = String(prevRaw).replace(/\D/g, '');
                    const inputDigits = String(value).replace(/\D/g, '');
                    let digits = inputDigits;

                    // Если цифры не изменились, но строка стала короче — считаем, что пользователь стёр суффикс,
                    // и удаляем последнюю цифру
                    if (prevDigits && inputDigits === prevDigits && String(value).length < String(prevRaw).length) {
                        digits = prevDigits.slice(0, -1);
                    }

                    nextValue = digits ? `${Number(digits)} шт.` : '';
                } else if (field === 'productPrice') {
                    // допускаем только цифры и запятые/точки
                    nextValue = String(value).replace(/[^\d.,]/g, '');
                } else if (field === 'deliveryTerm') {
                    const prevRaw = product.deliveryTerm ?? '';
                    const prevDigits = String(prevRaw).replace(/\D/g, '');
                    const inputDigits = String(value).replace(/\D/g, '');
                    let digits = inputDigits;

                    if (prevDigits && inputDigits === prevDigits && String(value).length < String(prevRaw).length) {
                        digits = prevDigits.slice(0, -1);
                    }

                    nextValue = digits ? `${Number(digits)} раб. дней` : '';
                }

                const updated = { ...product, [field]: nextValue };

                if (field === 'productName') {
                    updated.manufacturerName = '';
                    updated.categoryName = '';
                    updated.categoryId = '';
                    updated.categoryAssigned = false;
                    updated.isPlaced = false;
                    setActiveDraftProductId(productId);
                    setSearchState({ productId, query: value });
                }

                return updated;
            })
        );
    };

    useEffect(() => {
        const { productId, query } = searchState;
        const trimmedQuery = (query || '').trim();

        if (!productId || trimmedQuery.length < 3) {
            setHints([]);
            setHintsFetched(false);
            return;
        }

        setHintsFetched(false);
        let cancelled = false;

        const timeoutId = setTimeout(() => {
            fetchOrderProductHints(
                trimmedQuery,
                (list) => {
                    if (cancelled) {
                        return;
                    }
                    setHints(list);
                    setHintsFetched(true);
                },
                (error) => {
                    if (cancelled) {
                        return;
                    }
                    console.error('Ошибка загрузки подсказок по товару:', error);
                    setHints([]);
                    setHintsFetched(true);
                }
            );
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [searchState.productId, searchState.query]);

    useLayoutEffect(() => {
        if (!scrollAfterAddRef.current) {
            return;
        }
        scrollAfterAddRef.current = false;
        tableScrollRef.current?.scrollToBottom();
    }, [addedProducts, rowOrder]);

    const handleDraftCategoryChange = (productId, value) => {
        setAddedProducts((prev) =>
            prev.map((product) => {
                if (product.productId !== productId) return product;
                return { ...product, categoryName: value };
            })
        );
    };

    const handleHintClick = (hint) => {
        if (!activeDraftProductId || !hint) return;
        const partNumber = getHintPartNumber(hint);
        const realProductId = hint.productId || hint.id || activeDraftProductId;
        setAddedProducts((prev) =>
            prev.map((product) => {
                if (product.productId !== activeDraftProductId) return product;
                return {
                    ...product,
                    productId: realProductId,
                    productName: partNumber || product.productName || '',
                    manufacturerName: getHintManufacturer(hint),
                    categoryName: '',
                    categoryId: '',
                    categoryAssigned: false,
                    isPlaced: true,
                };
            })
        );
        setHints([]);
        setHintsFetched(false);
        if (!hint.productId && !hint.id) {
            return;
        }
        getFetchRequest(
            makeUrl(['/api/v1/category/by-product/', realProductId, '/']),
            (data) => {
                setAddedProducts((prev) =>
                    prev.map((product) => {
                        if (product.productId !== realProductId) return product;
                        return {
                            ...product,
                            categoryId: data && data.categoryId ? data.categoryId : '',
                            categoryName: data && data.categoryName ? data.categoryName : '',
                        };
                    })
                );
            },
            (error) => {
                console.error('Ошибка загрузки категории товара:', error);
            }
        );
    };

    const handleAssignCategory = (productId, categoryId) => {
        const category = flattenCategories(categoriesList).find((item) => item.id === categoryId);
        if (!category) {
            return;
        }
        setAddedProducts((prev) =>
            prev.map((product) => {
                if (product.productId !== productId) return product;
                return {
                    ...product,
                    categoryId,
                    categoryName: categoryDisplayName(category),
                    categoryAssigned: true,
                };
            })
        );
    };

    const closeProductHints = () => {
        setHints([]);
        setHintsFetched(false);
        setActiveDraftProductId(null);
    };

    const handleOpenCatalogAddModal = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setCatalogAddModal({
            open: true,
            partNumber: (searchState.query || '').trim(),
        });
        closeProductHints();
    };

    const handleCloseCatalogAddModal = () => {
        setCatalogAddModal({ open: false, partNumber: '' });
    };

    if (!order) return null;
    const handleShipToggle = (productKey) => {
        setShipActiveState((prev) => ({
            ...prev,
            [productKey]: !prev[productKey],
        }));
    };

    const handleSupplierOrderChange = (productKey, value) => {
        setSupplierOrderSelection((prev) => ({ ...prev, [productKey]: value }));
    };

    const handleCustomerChange = (customerId) => {
        setSelectedCustomerId(customerId);
        const customer = Array.isArray(customers)
            ? customers.find((c) => String(c.id) === String(customerId))
            : null;
        if (customer) {
            const userId = customer.userId || customer.user_id || null;
            setSelectedUserId(userId || null);
        } else {
            setSelectedUserId(null);
        }
    };

    const handleSubmitNewOrder = () => {
        if (!isNew || isSubmitting) return;
        if (!selectedCustomerId) {
            alert('Пожалуйста, выберите клиента перед проведением документа.');
            return;
        }

        const sourceProducts = productsWithDemo.length ? productsWithDemo : addedProducts;
        const productsPayload = (Array.isArray(sourceProducts) ? sourceProducts : []).map((p) => ({
            productId: p.productId,
            productCount: parseNumeric(p.productCount ?? ''),
            productPrice: parseNumeric(p.productPrice ?? ''),
            deliveryTermDays: parseDeliveryTermDays(p.deliveryTerm),
        }));

        const categorySingularsById = new Map();
        (Array.isArray(sourceProducts) ? sourceProducts : []).forEach((product) => {
            const nameSingular = (product.categoryName || '').trim();
            if (!product.categoryId || !nameSingular) {
                return;
            }
            categorySingularsById.set(product.categoryId, nameSingular);
        });

        const resolvedValidUntil = validUntil || getDefaultValidUntil(order);
        const body = {
            customerId: selectedCustomerId,
            userId: selectedUserId,
            products: productsPayload,
            categorySingulars: Array.from(categorySingularsById, ([id, nameSingular]) => ({
                id,
                nameSingular,
            })),
            productCategories: collectProductCategories(sourceProducts),
            currency,
            invoiceForm,
        };
        if (resolvedValidUntil) {
            body.validUntil = resolvedValidUntil;
        }

        setIsSubmitting(true);
        postFetchRequest(
            makeUrl([ADMIN_CREATE_ORDER]),
            body,
            (created) => {
                setIsSubmitting(false);
                if (onOrderCreated) {
                    onOrderCreated(created);
                }
            },
            (err) => {
                console.error(err);
                alert('Не удалось создать заказ.');
                setIsSubmitting(false);
            }
        );
    };

    const handleSubmitAddedProducts = () => {
        if (isNew || isSubmitting || !canAddProducts) return;
        if (!order?.id || order.id === 'new') return;

        const source = productsWithDemo.length ? productsWithDemo : addedProducts;
        const lineOrder = (Array.isArray(source) ? source : []).flatMap((product, index) => {
            const position = index + 1;
            if (product.isDraft) {
                if (!product.productId || String(product.productId).startsWith('draft-')) {
                    return [];
                }
                return [{
                    position,
                    productId: product.productId,
                    productCount: Math.round(parseNumeric(product.productCount ?? '')),
                    productPrice: parseNumeric(product.productPrice ?? ''),
                    deliveryTermDays: parseDeliveryTermDays(product.deliveryTerm),
                }];
            }
            if (!product.orderProductId) {
                return [];
            }
            return [{
                position,
                orderProductId: product.orderProductId,
            }];
        });

        const hasNewProduct = lineOrder.some((line) => line.productId);
        if (!lineOrder.length || (!orderDirty && !hasNewProduct)) {
            alert('Пожалуйста, добавьте хотя бы один товар в заказ.');
            return;
        }

        const categorySingularsById = new Map();
        source.forEach((product) => {
            const nameSingular = (product.categoryName || '').trim();
            if (!product.categoryId || !nameSingular) {
                return;
            }
            categorySingularsById.set(product.categoryId, nameSingular);
        });

        const body = {
            lineOrder,
            categorySingulars: Array.from(categorySingularsById, ([id, nameSingular]) => ({
                id,
                nameSingular,
            })),
            productCategories: collectProductCategories(source),
        };

        setIsSubmitting(true);
        patchFetchRequest(
            makeUrl([GET_ORDERS, order.id, '/']),
            body,
            (updated) => {
                setIsSubmitting(false);
                setOrderDirty(false);
                setSelectedRowKey(null);
                setAddedProducts([]);
                if (onOrderUpdated) {
                    onOrderUpdated(order.id, updated);
                }
            },
            (err) => {
                console.error(err);
                alert('Не удалось добавить товары в заказ.');
                setIsSubmitting(false);
            }
        );
    };


    // 🔧 Логируем структуру заказа для отладки цен в админке
    console.log(`📋 [ADMIN ORDER DETAIL] Order structure:`, {
        orderId: order.id,
        status: order.status,
        totalCost: order.totalCost,
        productsCount: order.products?.length || 0,
        currency: order.currency,
    });
    
    if (order.products && order.products.length > 0) {
        order.products.forEach((product, index) => {
            console.log(`📦 [ADMIN ORDER PRODUCT ${index}] Structure:`, {
                productName: product.productName || product.name,
                productCount: product.productCount,
                quantity: product.quantity,      // Старое поле (fallback)
                count: product.count,           // Старое поле (fallback)
                productPrice: product.productPrice,  // Правильное поле
                price: product.price,           // Старое поле (undefined?)
                manufacturerName: product.manufacturerName,
                allFields: Object.keys(product),
                fullProduct: product,
            });
        });
    }

    const productHintsOpen = Boolean(activeDraftProductId) && (hints.length > 0 || hintsFetched);
    const productHints = productHintsOpen ? (
        <OrderProductHintsLayer anchorRef={productAnchorRef} onAnchorHidden={closeProductHints}>
            {hints.length > 0 ? (
                hints.map((hint, hintIndex) => {
                    const partNumber = getHintPartNumber(hint);
                    if (!partNumber) {
                        return null;
                    }
                    const duplicatePartNumbers = getDuplicateHintPartNumbers(hints);
                    const label = formatOrderHintLabel(
                        hint,
                        duplicatePartNumbers.has(partNumber.toLowerCase())
                    );
                    const hintKey = `${hint.productId || hint.id || partNumber}-${hintIndex}`;
                    return (
                        <div
                            key={hintKey}
                            className="order-product-hint-item"
                            onMouseDown={() => handleHintClick(hint)}
                        >
                            <span className="order-product-hint-part">
                                {label}
                            </span>
                        </div>
                    );
                })
            ) : (
                <div className="order-product-hint-item order-product-hint-item--missing">
                    <span className="order-product-hint-part order-product-hint-part--missing">
                        Нет в каталоге
                    </span>
                </div>
            )}
            <button
                type="button"
                className="order-product-hint-add"
                onMouseDown={handleOpenCatalogAddModal}
                aria-label="Добавить товар в каталог"
            >
                <span className="order-product-hint-add__icon">+</span>
            </button>
        </OrderProductHintsLayer>
    ) : null;

    return (
        <div className="order-detail-container">
            <div className="order-detail-header">
                <button className="order-detail-back-btn" onClick={onBack}>
                <svg width="31" height="12" viewBox="0 0 31 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.219667 4.99262C-0.073225 5.28551 -0.073225 5.76039 0.219667 6.05328L4.99264 10.8263C5.28553 11.1191 5.76041 11.1191 6.0533 10.8263C6.34619 10.5334 6.34619 10.0585 6.0533 9.76559L1.81066 5.52295L6.0533 1.28031C6.34619 0.987415 6.34619 0.512542 6.0533 0.219648C5.76041 -0.073245 5.28553 -0.073245 4.99264 0.219648L0.219667 4.99262ZM30.75 5.52295V4.77295L0.749998 4.77295V5.52295V6.27295L30.75 6.27295V5.52295Z" fill="#666666"/>
                </svg>

                    Назад
                </button>
            </div>
            
            {/* Блок сведений и оплат/отгрузок */}
            <div className="order-manager">
                <header className="order-header">
                    <div className="order-header__field">
                        <label className="order-header__label">Заказ клиента:</label>
                        <input
                            type="text"
                            className="order-header__input order-header__input--readonly"
                                value={order.order_number || order.orderNumber || ''}
                            readOnly
                        />
                    </div>

                    <div className="order-header__field">
                        <label className="order-header__label">От:</label>
                        <div className="order-header__input-wrapper">
                            <input
                                type="date"
                                className="order-header__input"
                                defaultValue={formatDateInput(order.date_creation || order.dateCreation)}
                            />
                        </div>
                    </div>

                    <div className="order-header__field order-header__field--grow">
                        <label className="order-header__label">Клиент:</label>
                        <div className="order-header__select-wrapper">
                            {isNew ? (
                                <select
                                    className="order-header__select"
                                    value={selectedCustomerId || ''}
                                    onChange={(e) => handleCustomerChange(e.target.value)}
                                >
                                    <option value="">Выберите клиента</option>
                                    {Array.isArray(customers) && customers.map((customer) => (
                                        <option key={customer.id} value={customer.id}>
                                            {customer.name || customer.legalAddress || customer.tin || customer.id}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <select className="order-header__select" defaultValue={order.customerName || order.customer_id || ''}>
                                    <option>{order.customerName || order.customer_id || ''}</option>
                                </select>
                            )}
                            <svg className="order-header__icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L5 5L9 1" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                    </div>

                    <div className="order-header__field" style={{ position: 'absolute', right: '30px' }}>
                        <label className="order-header__label">Действует до:</label>
                        <div className="order-header__input-wrapper">
                            <input
                                type="date"
                                className="order-header__input order-header__input--date"
                                value={validUntil}
                                onChange={(e) => handleValidUntilChange(e.target.value)}
                            />
                        </div>
                    </div>
                </header>

                <main className="order-content">
                    <div className="order-section">
                        <h3 className="order-section__title">Оплаты по заказу:</h3>
                        <div className="order-table-container">
                            <table className="order-table">
                                <thead>
                                    <tr>
                                        <th>№</th>
                                        <th>Номер п/п</th>
                                        <th>Дата</th>
                                        <th>% Оплаты</th>
                                        <th>Сумма, ₽</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(order.payments) && order.payments.length > 0 ? (
                                        order.payments.map((payment, paymentIndex) => (
                                            <tr key={payment.documentNumber || paymentIndex}>
                                                <td>{paymentIndex + 1}</td>
                                                <td>{payment.paymentOrderNumber || '-'}</td>
                                                <td>{formatDate(payment.paymentDate)}</td>
                                                <td>{formatPercent(payment.percent)}</td>
                                                <td>{formatCurrency(payment.amount)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td>-</td>
                                            <td>-</td>
                                            <td>-</td>
                                            <td>-</td>
                                            <td>-</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            <div className="order-table-summary">
                                Общая сумма оплат, ₽: {formatCurrency(order.paidAmount ?? 0) || '0,00'}
                            </div>
                        </div>
                    </div>

                    <div className="order-section">
                        <h3 className="order-section__title">Отгрузки по заказу:</h3>
                        <div className="order-table-container">
                            <table className="order-table">
                                <thead>
                                    <tr>
                                        <th>№</th>
                                        <th>УПД (Фактура)</th>
                                        <th>Дата</th>
                                        <th>% Отгрузки</th>
                                        <th>Сумма с НДС, ₽</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>-</td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td>-</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className="order-table-summary">Общая сумма отгруженного товара, ₽: 0,00</div>
                        </div>
                    </div>
                </main>

                <footer className="order-footer">
                    <div className="order-footer__actions">
                        <button
                            className="order-button"
                            type="button"
                            onClick={
                                isNew
                                    ? handleSubmitNewOrder
                                    : (canAddProducts && (orderDirty || addedProducts.length > 0) ? handleSubmitAddedProducts : undefined)
                            }
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Загрузка…' : 'Провести документ'}
                        </button>
                        <button className="order-button">Реализация товаров</button>
                        {canReorder && (
                            <div className="order-move-buttons" data-order-move-buttons>                           
                                <button
                                    type="button"
                                    className="order-button order-move-button"
                                    aria-label="Переместить товар вниз"
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={() => moveSelectedRow(1)}
                                >
                                    <svg width="12" height="15" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5.52344 0L4.77344 0L4.77344 15H5.52344H6.27344L6.27344 0L5.52344 0ZM4.99311 15.5303C5.286 15.8232 5.76087 15.8232 6.05377 15.5303L10.8267 10.7574C11.1196 10.4645 11.1196 9.98959 10.8267 9.6967C10.5338 9.40381 10.059 9.40381 9.76608 9.6967L5.52344 13.9393L1.2808 9.6967C0.987904 9.40381 0.51303 9.40381 0.220137 9.6967C-0.0727568 9.98959 -0.0727568 10.4645 0.220137 10.7574L4.99311 15.5303Z" fill="#222222"/>
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    className="order-button order-move-button"
                                    aria-label="Переместить товар вверх"
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={() => moveSelectedRow(-1)}
                                >
                                    <svg width="12" height="15" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5.52344 15.75H6.27344L6.27344 0.75H5.52344H4.77344L4.77344 15.75H5.52344ZM6.05377 0.21967C5.76087 -0.0732231 5.286 -0.0732231 4.99311 0.21967L0.220137 4.99264C-0.0727568 5.28553 -0.0727568 5.76041 0.220137 6.0533C0.51303 6.34619 0.987904 6.34619 1.2808 6.0533L5.52344 1.81066L9.76608 6.0533C10.059 6.34619 10.5338 6.34619 10.8267 6.0533C11.1196 5.76041 11.1196 5.28553 10.8267 4.99264L6.05377 0.21967Z" fill="#222222"/>
                                    </svg>

                                </button>
                            </div>
                        )}
                    </div>
                    {!isNew && (order.has_invoice || order.hasInvoice) && (
                        <button
                            className="order-button order-print-button"
                            type="button"
                            onClick={() => onPrintInvoice && onPrintInvoice(order.id || order.orderId)}
                        >
                            Распечатать
                            <svg
                                className="order-print-button__icon"
                                width="18"
                                height="18"
                                viewBox="0 0 554.625 554.625"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g>
                                    <g>
                                        <rect x="133.875" y="401.625" width="286.875" height="19.125" />
                                        <rect x="133.875" y="459" width="286.875" height="19.125" />
                                        <path d="M497.25,191.25H459V57.375c0-21.038-17.213-38.25-38.25-38.25H133.875c-21.038,0-38.25,17.212-38.25,38.25V191.25h-38.25
		                                C24.862,191.25,0,216.112,0,248.625V420.75c0,32.513,24.862,57.375,57.375,57.375h38.25v19.125c0,21.037,17.212,38.25,38.25,38.25
		                                H420.75c21.037,0,38.25-17.213,38.25-38.25v-19.125h38.25c32.513,0,57.375-24.862,57.375-57.375V248.625
		                                C554.625,216.112,529.763,191.25,497.25,191.25z M114.75,57.375c0-9.562,7.65-19.125,19.125-19.125H420.75
		                                c9.562,0,19.125,7.65,19.125,19.125V191.25H114.75V57.375z M439.875,497.25c0,9.562-7.65,19.125-19.125,19.125H133.875
		                                c-9.562,0-19.125-7.65-19.125-19.125V363.375h325.125V497.25z M535.5,420.75c0,21.037-17.213,38.25-38.25,38.25H459V344.25H95.625
		                                V459h-38.25c-21.038,0-38.25-17.213-38.25-38.25V248.625c0-21.038,17.212-38.25,38.25-38.25H497.25
		                                c21.037,0,38.25,17.212,38.25,38.25V420.75z" />
                                        <circle cx="439.875" cy="267.75" r="19.125" />
                                    </g>
                                </g>
                            </svg>
                            
                        </button>
                    )}
                    <div className="order-footer__controls order-footer__controls--currency">
                        <label className="order-header__label">Бланк:</label>
                        <div className="order-header__select-wrapper order-header__select-wrapper--form">
                            <select
                                className="order-header__select order-header__select--form"
                                value={invoiceForm}
                                onChange={(e) => handleInvoiceFormChange(e.target.value)}
                            >
                                <option value="offer">Счет-оферта</option>
                                <option value="bill">Счет</option>
                            </select>
                            <svg className="order-header__icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L5 5L9 1" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        {isNew && (
                            <>
                                <label className="order-header__label order-header__label--currency">Валюта:</label>
                                <div className="order-header__select-wrapper">
                                    <select
                                        className="order-header__select"
                                        value={currency}
                                        onChange={(e) => setCurrency(e.target.value)}
                                    >
                                        <option value="RUB">RUB</option>
                                        <option value="USD">USD</option>
                                        <option value="CNY">CNY</option>
                                    </select>
                                    <svg className="order-header__icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 1L5 5L9 1" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            </>
                        )}
                    </div>
                    

                    {!isNew && !(
                        Boolean(order.createdFromAdmin ?? order.created_from_admin)
                        && Boolean(order.has_invoice ?? order.hasInvoice)
                    ) && (
                        <div className="order-footer__controls">
                            <label className="order-toggle">
                                <span className="order-toggle__label">Одобрить счет</span>
                                <input
                                    type="checkbox"
                                    className="order-toggle__input"
                                    checked={isInvoiceToggleOn}
                                    disabled={isInvoiceToggleLoading}
                                    onChange={async (e) => {
                                        const checked = e.target.checked;
                                        if (!checked) {
                                            setIsInvoiceToggleOn(false);
                                            return;
                                        }
                                        if (!onSendInvoice) return;
                                        try {
                                            setIsInvoiceToggleLoading(true);
                                            await onSendInvoice(order.id || order.orderId);
                                            setIsInvoiceToggleOn(true);
                                        } catch (err) {
                                            setIsInvoiceToggleOn(false);
                                        } finally {
                                            setIsInvoiceToggleLoading(false);
                                        }
                                    }}
                                />
                                <span className="order-toggle__slider"></span>
                            </label>
                        </div>
                    )}
                </footer>
            </div>

            {/* Список товаров заказа (клиентские стили) */}
            {productsWithDemo.length > 0 && (
                <div className="order-detail-products">
                    <div className={`clients-table clients-table--order clients-table--order-customer ${(isNew || addedProducts.length > 0) ? 'clients-table--order-edit' : ''}`}>
                        <div className="clients-table__header">
                            <div className="clients-table__row">
                                <div className="clients-table__cell clients-table__cell--id">№</div>
                                <div className="clients-table__cell">Товар</div>
                                <div className="clients-table__cell">Кол-во</div>
                                <div className="clients-table__cell">Цена</div>
                                <div className="clients-table__cell">Сумма</div>
                                <div className="clients-table__cell">НДС</div>
                                <div className="clients-table__cell">Срок поставки</div>
                                <div className="clients-table__cell">Дата поставки</div>
                                <div className="clients-table__cell">Заказано</div>
                                <div className="clients-table__cell clients-table__cell--ship">Отгрузить</div>
                                <div className="clients-table__cell clients-table__cell--center">Статус товара</div>
                                <div className="clients-table__cell clients-table__cell--center">Заказы поставщиков</div>
                                <div className="clients-table__cell">Резерв</div>
                            </div>
                        </div>
                        <ClientsTableOverlayScroll ref={tableScrollRef} overlay={productHints}>
                            {productsWithDemo.map((product, index) => {
                                const rawQty = product.productCount ?? product.quantity ?? product.count ?? 0;
                                const qty = parseNumeric(rawQty);
                                const rawPrice = product.productPrice ?? product.price ?? 0;
                                const price = parseNumeric(rawPrice);
                                const sum = qty * price;
                                const reservedFromSuppliers = parseNumeric(product.supplierReservedQty ?? 0);
                                const notCoveredQty = !isNew ? Math.max(qty - reservedFromSuppliers, 0) : 0;
                                const vatPercentRaw = product.vat ?? product.vatPercent ?? product.nds ?? DEFAULT_VAT_PERCENT;
                                const vatPercentNum = Number(String(vatPercentRaw).replace('%', ''));
                                const vatPercent = Number.isNaN(vatPercentNum) ? DEFAULT_VAT_PERCENT : vatPercentNum;
                                // Предполагаем, что цена с НДС; выделяем НДС из суммы позиции
                                const ndsValue = sum ? sum * vatPercent / (100 + vatPercent) : 0;
                                const deliveryTerm = product.deliveryTerm ?? product.leadTime ?? product.deliveryTime ?? '';
                                const deliveryDate = formatDate(product.deliveryDate ?? product.dateDelivery, true);
                                const orderedQty = formatPiecesWithDefault(product.ordered ?? product.orderedQty ?? '');
                                const toShipQty = product.toShip ?? product.ship ?? product.shipQty ?? '';
                                const ourStock = product.ourStock ?? product.stock ?? '';
                                const supplierStock = product.supplierStock ?? product.vendorStock ?? product.distributorStock ?? '';
                                const hasOurStock = String(ourStock ?? '').trim() !== '';
                                const hasSupplierStock = String(supplierStock ?? '').trim() !== '';
                                const supplierStatusList = Array.isArray(product.supplierOrders) ? product.supplierOrders : [];
                                const stockStatusText = (() => {
                                    if (hasOurStock && hasSupplierStock) {
                                        return `${ourStock} / ${supplierStock}`;
                                    }
                                    if (hasOurStock) {
                                        return ourStock;
                                    }
                                    if (hasSupplierStock) {
                                        return supplierStock;
                                    }
                                    return '-';
                                })();
                                const reserve = formatPiecesWithDefault(product.reserve ?? product.reserved ?? '');
                                const rowKey = getOrderRowKey(product);
                                const productKey = supplierOptionKey(product);
                                const isRowSelected = selectedRowKey === rowKey;
                                const showSupplierSelect = isNew || product.isDraft;
                                const supplierOptions = showSupplierSelect
                                    ? (supplierOptionsByProduct[productKey] || [])
                                    : (Array.isArray(product.supplierOrders) ? product.supplierOrders : []);
                                const supplierOrders = supplierOrderSelection[productKey] || (supplierOptions[0]?.supplierOrderProductId || '');
                                const isShipActive = Boolean(shipActiveState[productKey]);

                                return (
                                    <div
                                        key={rowKey}
                                        className={`clients-table__row${isRowSelected ? ' clients-table__row--selected' : ''}`}
                                        data-order-row
                                        onMouseDown={(event) => {
                                            const target = event.target;
                                            if (!(target instanceof Element)) {
                                                return;
                                            }
                                            const interactive = target.closest('input, button, select, textarea');
                                            if (interactive && !interactive.matches('input.order-product-input[readonly]')) {
                                                return;
                                            }
                                            if (!canReorder) {
                                                return;
                                            }
                                            setSelectedRowKey(rowKey);
                                        }}
                                    >
                                        <div className="clients-table__cell clients-table__cell--id order-row-number">
                                            {index + 1}
                                        </div>
                                        <div className={`clients-table__cell ${product.isDraft ? 'clients-table__cell--product-edit clients-table__cell--product-input' : ''} ${product.categoryName ? 'clients-table__cell--product-with-category' : ''}`}>
                                            {product.isDraft ? (
                                                <div
                                                    className={`order-product-input-wrapper${product.isPlaced ? ' order-product-input-wrapper--placed' : ''}`}
                                                    ref={activeDraftProductId === product.productId ? productAnchorRef : null}
                                                >
                                                    <input
                                                        type="text"
                                                        placeholder="-"
                                                        className="order-header__input order-product-input"
                                                        value={formatProductFieldValue(product.productName, product.manufacturerName)}
                                                        onChange={(e) =>
                                                            handleDraftProductChange(
                                                                product.productId,
                                                                'productName',
                                                                e.target.value
                                                            )
                                                        }
                                                        onBlur={() => {
                                                            setTimeout(closeProductHints, 100);
                                                        }}
                                                    />
                                                    {product.isPlaced && !product.categoryId ? (
                                                        <select
                                                            className="order-product-category order-product-category--select"
                                                            value=""
                                                            onChange={(e) => handleAssignCategory(product.productId, e.target.value)}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                        >
                                                            <option value="">без категории</option>
                                                            {flattenCategories(categoriesList).map((category) => (
                                                                <option key={category.id} value={category.id}>
                                                                    {category.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : product.isPlaced && (product.categoryId || product.categoryName) ? (
                                                        <input
                                                            type="text"
                                                            className="order-product-category"
                                                            value={product.categoryName || ''}
                                                            onChange={(e) => handleDraftCategoryChange(product.productId, e.target.value)}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                        />
                                                    ) : null}
                                                </div>
                                            ) : (
                                                <div className={`order-product-input-wrapper${product.categoryName ? ' order-product-input-wrapper--placed' : ''}`}>
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        className="order-header__input order-product-input"
                                                        value={formatProductFieldValue(
                                                            product.productName || product.name,
                                                            product.manufacturerName
                                                        )}
                                                    />
                                                    {product.categoryName ? (
                                                        <div className="order-product-category">{product.categoryName}</div>
                                                    ) : null}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`clients-table__cell ${product.isDraft ? 'clients-table__cell--product-input' : ''}`}>
                                            {product.isDraft ? (
                                                <input
                                                    type="text"
                                                    className="order-header__input order-product-input"
                                                    value={product.productCount ?? ''}
                                                    onChange={(e) =>
                                                        handleDraftProductChange(
                                                            product.productId,
                                                            'productCount',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            ) : (
                                                formatPiecesWithDefault(qty)
                                            )}
                                        </div>
                                        <div className={`clients-table__cell ${product.isDraft ? 'clients-table__cell--product-input' : ''}`}>
                                            {product.isDraft ? (
                                                <input
                                                    type="text"
                                                    className="order-header__input order-product-input"
                                                    value={product.productPrice ?? ''}
                                                    onChange={(e) =>
                                                        handleDraftProductChange(
                                                            product.productId,
                                                            'productPrice',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            ) : (
                                                formatCurrency(price, order.currency)
                                            )}
                                        </div>
                                        <div className="clients-table__cell">{formatCurrency(sum, order.currency)}</div>
                                        <div className="clients-table__cell">{formatCurrency(ndsValue, order.currency)}</div>
                                        <div className={`clients-table__cell ${product.isDraft ? 'clients-table__cell--product-input' : ''}`}>
                                            {product.isDraft ? (
                                                <input
                                                    type="text"
                                                    className="order-header__input order-product-input"
                                                    value={product.deliveryTerm ?? ''}
                                                    onChange={(e) =>
                                                        handleDraftProductChange(
                                                            product.productId,
                                                            'deliveryTerm',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            ) : (
                                                deliveryTerm
                                            )}
                                        </div>
                                        <div className="clients-table__cell">{deliveryDate}</div>
                                        <div className="clients-table__cell">{orderedQty}</div>
                                        <div className="clients-table__cell clients-table__cell--ship">
                                            <button
                                                type="button"
                                                className={`tristate-switch__thumb order-ship-toggle ${isShipActive ? 'order-ship-toggle--active' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleShipToggle(productKey);
                                                }}
                                                aria-pressed={isShipActive}
                                                aria-label={`Отгрузить ${toShipQty || '0'}`}
                                            />
                                        </div>
                                        <div
                                            className="clients-table__cell clients-table__cell--product-status"
                                            style={
                                                !isNew && supplierStatusList.length > 1
                                                    ? {
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                        flexDirection: 'column',
                                                        minHeight: `${supplierStatusList.length * 35}px`,
                                                    }
                                                    : undefined
                                            }
                                        >
                                            {(!isNew && supplierStatusList.length > 0)
                                                ? supplierStatusList.map((link, idx) => {
                                                    const badgeText = link.statusName || stockStatusText;
                                                    const badgeColor = link.statusColor || null;
                                                    return (
                                                        <div
                                                            key={`${productKey}-status-${idx}`}
                                                            className="product-status-badge"
                                                            style={badgeColor ? { backgroundColor: badgeColor } : undefined}
                                                        >
                                                            {badgeText}
                                                        </div>
                                                    );
                                                })
                                                : (
                                                    <div
                                                        className="product-status-badge"
                                                    >
                                                        {stockStatusText}
                                                    </div>
                                                )
                                            }
                                        </div>
                                        <div
                                            className="clients-table__cell cell-supplier-select"
                                            style={
                                                !showSupplierSelect
                                                    ? (() => {
                                                        const linksCount = Array.isArray(product.supplierOrders) ? product.supplierOrders.length : 0;
                                                        const extra = notCoveredQty > 0 ? 1 : 0;
                                                        const totalBlocks = linksCount + extra;
                                                        return totalBlocks > 1
                                                            ? { minHeight: `${totalBlocks * 35}px` }
                                                            : undefined;
                                                    })()
                                                    : undefined
                                            }
                                        >
                                            {showSupplierSelect ? (
                                                <>
                                                    <select
                                                        value={supplierOrders}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleSupplierOrderChange(productKey, e.target.value);
                                                        }}
                                                    >
                                                        {supplierOptions.length === 0 && (
                                                            <option value="">Не выбран</option>
                                                        )}
                                                        {supplierOptions.map((opt) => (
                                                            <option key={opt.supplierOrderProductId} value={opt.supplierOrderProductId}>
                                                                {`${opt.externalNumber || opt.orderNumber} · всего ${opt.qtyTotal} шт · доступно ${opt.availableQty} шт`}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <svg className="order-header__icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M1 1L5 5L9 1" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </>
                                            ) : (
                                                <div className="supplier-links-list">
                                                    {Array.isArray(product.supplierOrders) && product.supplierOrders.length > 0 ? (
                                                        <>
                                                            {product.supplierOrders.map((opt) => (
                                                                <div
                                                                    key={opt.supplierOrderProductId}
                                                                    className="supplier-link-item supplier-link-item--open"
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (!opt.supplierOrderId) {
                                                                            return;
                                                                        }
                                                                        navigate(`/purchases/supplier-orders?orderId=${encodeURIComponent(opt.supplierOrderId)}`);
                                                                    }}
                                                                >
                                                                    {opt.externalNumber || opt.orderNumber}
                                                                </div>
                                                            ))}
                                                            {notCoveredQty > 0 && (
                                                                <div className="supplier-link-item red">
                                                                    {`Не обеспечено ${notCoveredQty} шт`}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {notCoveredQty > 0 ? (
                                                                <div className="supplier-link-item red">
                                                                    {`Не обеспечено ${notCoveredQty} шт`}
                                                                </div>
                                                            ) : (
                                                                <div className="supplier-link-item">
                                                                    Не связан
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="clients-table__cell reserved">
                                            {showSupplierSelect ? (
                                                reserve
                                            ) : (
                                                <div className="supplier-reserve-list">
                                                    {Array.isArray(product.supplierOrders) && product.supplierOrders.length > 0 ? (
                                                        <>
                                                            {product.supplierOrders.map((opt) => {
                                                                const qtyText = opt.qty != null ? opt.qty : 0;
                                                                return (
                                                                    <div
                                                                        key={`reserve-${opt.supplierOrderProductId}`}
                                                                    >
                                                                        {`${qtyText} шт.`}
                                                                    </div>
                                                                );
                                                            })}
                                                            {notCoveredQty > 0 && (
                                                                <div>
                                                                    0 шт.
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div>
                                                            0 шт.
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {product.isDraft && (!isNew || (addedProducts && addedProducts.length > 1)) && (
                                                <button
                                                    type="button"
                                                    className="order-row-remove-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveDraftProduct(product.productId);
                                                    }}
                                                    aria-label="Удалить строку"
                                                >
                                                    <span className="order-row-remove-btn__icon">×</span>
                                                </button>
                                            )}
                                            {!isNew && !product.isDraft && product.orderProductId && productsWithDemo.filter((item) => !item.isDraft).length > 1 && (
                                                <button
                                                    type="button"
                                                    className="order-row-remove-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveSavedProduct(
                                                            product.orderProductId,
                                                            product.productName || product.name
                                                        );
                                                    }}
                                                    aria-label="Удалить строку"
                                                >
                                                    <span className="order-row-remove-btn__icon">×</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </ClientsTableOverlayScroll>
                    </div>
                </div>
            )}
            <div className="order-new__bottom">
                {canAddProducts && (
                    <button
                        className="order-button"
                        type="button"
                        onClick={handleAddProductRow}
                    >
                        Добавить товар +
                    </button>
                )}
                <div className="order-summary">
                    {(() => {
                        // В режиме создания считаем итоги по текущим строкам (productsWithDemo),
                        // в режиме просмотра — по данным заказа.
                        const summarySource = (isNew || addedProducts.length > 0)
                            ? { ...order, products: productsWithDemo }
                            : order;
                        const summary = calculateOrderSummary(summarySource);
                        const summaryCurrency = isNew ? currency : (order.currency || 'RUB');
                        return (
                            <>
                                <div className="order-summary__row">
                                    <span className="order-summary__label">НДС:</span>
                                    <div className="order-summary__value-wrapper">
                                        <span className="order-summary__value">
                                            {summary.vat !== null
                                                ? formatCurrency(summary.vat, summaryCurrency)
                                                : ''}
                                        </span>
                                        <span className="order-summary__currency">{summaryCurrency}</span>
                                    </div>
                                </div>
                                <div className="order-summary__row">
                                    <span className="order-summary__label">Всего с НДС:</span>
                                    <div className="order-summary__value-wrapper">
                                        <span className="order-summary__value">
                                            {formatCurrency(summary.total, summaryCurrency)}
                                        </span>
                                        <span className="order-summary__currency">{summaryCurrency}</span>
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </div>
            </div>
            {catalogAddModal.open && (
                <div className="order-product-hint-modal-overlay">
                    <div
                        className="order-product-hint-modal"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="order-product-hint-modal__header">
                            <span>
                                Добавление в каталог партномера {catalogAddModal.partNumber}
                            </span>
                            <button
                                type="button"
                                className="order-product-hint-modal__close"
                                onClick={handleCloseCatalogAddModal}
                                aria-label="Закрыть"
                            >
                                ×
                            </button>
                        </div>
                        <div className="order-product-hint-modal__body">
                            <AutobaseRunPanel
                                initialPartNumber={catalogAddModal.partNumber}
                                autoStart
                                onAddToOrder={handleCloseCatalogAddModal}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const OrdersTab = ({ isCollapsed }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [ordersList, setOrdersList] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [approveLoading, setApproveLoading] = useState({});
    const [pendingApprove, setPendingApprove] = useState({});
    const [customers, setCustomers] = useState([]);

    useEffect(() => {
        loadOrders();
        loadCustomers();
    }, []);

    useEffect(() => {
        const orderId = searchParams.get('orderId');
        if (!orderId) {
            return;
        }
        getFetchRequest(
            makeUrl([GET_ORDERS, orderId, '/']),
            (fullOrder) => {
                setSelectedOrder({ ...fullOrder, isNew: false });
                setSearchParams({}, { replace: true });
            },
            (err) => {
                console.error('Ошибка загрузки деталей заказа:', err);
                alert('Не удалось загрузить детали заказа.');
                setSearchParams({}, { replace: true });
            }
        );
    }, [searchParams, setSearchParams]);

    const handleDeleteOrder = (order, event) => {
        event.stopPropagation();
        const orderNumber = order.order_number ?? order.orderNumber ?? '';
        const confirmed = window.confirm(
            `Удалить заказ клиента № ${orderNumber}? Связи с заказами поставщиков будут сняты, сами заказы поставщиков останутся.`
        );
        if (!confirmed) return;
        deleteFetchRequest(
            makeUrl([GET_ORDERS, order.id, '/']),
            () => {
                setOrdersList((prev) => prev.filter((item) => item.id !== order.id));
            },
            () => {
                alert('Не удалось удалить заказ');
            }
        );
    };

    const loadOrders = () => {
        setIsLoading(true);
        setError(null);
        
        const successCallback = (response) => {
            console.log('API Response:', response);
            console.log('Response type:', typeof response);
            console.log('Is array:', Array.isArray(response));
            setOrdersList(response || []);
            setIsLoading(false);
        };

        const handleError = (err) => {
            console.error('Full error object:', err);
            console.error('Error message:', err.message);
            console.error('Error stack:', err.stack);
            setError('Ошибка загрузки заказов: ' + (err.message || 'Неизвестная ошибка'));
            setIsLoading(false);
        };
        const url = makeUrl([GET_ORDERS, '?view=summary']);
        console.log('Making request to:', url);
        getFetchRequest(url, successCallback, handleError);
    };

    const loadCustomers = () => {
        const url = makeUrl([MAKE_CUSTOMER_REQUEST]);
        const successCallback = (response) => {
            if (Array.isArray(response)) {
                setCustomers(response);
            } else {
                setCustomers([]);
            }
        };
        const handleError = (err) => {
            console.error('Ошибка загрузки клиентов:', err);
            setCustomers([]);
        };
        getFetchRequest(url, successCallback, handleError);
    };

    const handleSendInvoice = (orderId) => {
        if (!orderId) return Promise.reject(new Error('orderId is missing'));
        const url = makeUrl([GET_ORDERS, orderId, '/invoice/pdf/generate/']);
        return new Promise((resolve, reject) => {
            postFetchRequest(
                url,
                {},
                () => {
                    alert('Счёт сформирован и сохранён как PDF.');
                    setSelectedOrder((prev) => {
                        if (!prev) return prev;
                        const same = String(prev.id || prev.orderId) === String(orderId);
                        if (!same) return prev;
                        return { ...prev, has_invoice: true, hasInvoice: true };
                    });
                    loadOrders();
                    resolve();
                },
                (err) => {
                    console.error(err);
                    alert('Не удалось сформировать счёт');
                    reject(err || new Error('Не удалось сформировать счёт'));
                },
                true
            );
        });
    };

    const handlePrintInvoice = async (orderId) => {
        if (!orderId) return;
        try {
            const url = makeUrl([GET_ORDERS, orderId, '/invoice/pdf/']);
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                cache: 'no-store',
            });
            if (!response.ok) {
                throw new Error('Invoice not ready');
            }
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const printWindow = window.open(blobUrl);
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.focus();
                    printWindow.print();
                };
            }
        } catch (e) {
            // Сообщаем админу, что счёт ещё не готов или недоступен
            alert('Счёт пока недоступен. Попробуйте позже.');
        }
    };

    const handleApproveInvoice = async (order) => {
        if (!order?.id && !order?.orderId) return;
        const id = order.id || order.orderId;
        try {
            setPendingApprove((prev) => ({ ...prev, [id]: true }));
            setApproveLoading((prev) => ({ ...prev, [id]: true }));
            await handleSendInvoice(id);
            // локально помечаем как имеющий счёт
            setOrdersList((prev) =>
                prev.map((o) =>
                    String(o.id || o.orderId) === String(id)
                        ? { ...o, has_invoice: true, hasInvoice: true, status: o.status === 'PLACED' ? 'AWAITING_PAYMENT' : o.status }
                        : o
                )
            );
            setSelectedOrder((prev) => {
                if (!prev) return prev;
                const same = String(prev.id || prev.orderId) === String(id);
                if (!same) return prev;
                return { ...prev, has_invoice: true, hasInvoice: true, status: prev.status === 'PLACED' ? 'AWAITING_PAYMENT' : prev.status };
            });
        } catch (err) {
            // уже залогировано в handleSendInvoice
        } finally {
            setApproveLoading((prev) => ({ ...prev, [id]: false }));
            setPendingApprove((prev) => ({ ...prev, [id]: false }));
        }
    };

    const handleOrderClick = (order) => {
        if (!order || !order.id) {
            return;
        }
        setSearchParams({ orderId: String(order.id) });
    };

    const handleCreateOrder = () => {
        const now = new Date();
        const draftOrder = {
            id: 'new',
            order_number: '',
            date_creation: now.toISOString(),
            customerName: '',
            status: 'CREATED',
            currency: 'RUB',
            invoiceForm: 'offer',
            products: [],
            totalCost: 0,
            isNew: true,
        };
        setSelectedOrder(draftOrder);
    };

    const handleBackToOrders = () => {
        setSelectedOrder(null);
    };

    const handleOrderUpdated = (orderId, updatedOrder) => {
        if (!orderId) return;
        if (updatedOrder && (updatedOrder.id || updatedOrder.orderId)) {
            setSelectedOrder({ ...updatedOrder, isNew: false });
            loadOrders();
            return;
        }
        getFetchRequest(
            makeUrl([GET_ORDERS, orderId, '/']),
            (fullOrder) => {
                setSelectedOrder({ ...fullOrder, isNew: false });
                loadOrders();
            },
            () => {
                alert('Товар удалён, но не удалось обновить заказ.');
                loadOrders();
            }
        );
    };

    const handleInvoiceFormSaved = (orderId, nextInvoiceForm) => {
        setSelectedOrder((prev) => {
            if (!prev || String(prev.id) !== String(orderId)) {
                return prev;
            }
            return { ...prev, invoiceForm: nextInvoiceForm, invoice_form: nextInvoiceForm };
        });
    };

    const handleValidUntilSaved = (orderId, nextValidUntil) => {
        setSelectedOrder((prev) => {
            if (!prev || String(prev.id) !== String(orderId)) {
                return prev;
            }
            return { ...prev, validUntil: nextValidUntil, valid_until: nextValidUntil };
        });
    };

    const handleOrderCreated = (createdOrder) => {
        if (!createdOrder) return;
        setSelectedOrder({ ...createdOrder, isNew: false });
        loadOrders();
        alert('Заказ успешно создан.');
    };

    if (searchParams.get('orderId') && !selectedOrder) {
        return null;
    }

    if (isLoading && !selectedOrder) {
        return (
            <section className={`page-content ${isCollapsed === null ? '' : isCollapsed ? 'page-content_collapsed' : 'page-content_full-width'}`}>
                <div className="loading">Загрузка заказов...</div>
            </section>
        );
    }

    if (error && !selectedOrder) {
        return (
            <section className={`page-content ${isCollapsed === null ? '' : isCollapsed ? 'page-content_collapsed' : 'page-content_full-width'}`}>
                <div className="error">
                    <p>{error}</p>
                    <button onClick={loadOrders} className="retry-button">Повторить</button>
                </div>
            </section>
        );
    }

    const activeOrders = ordersList.filter(
        (order) => order?.status && !['CREATED', 'DELETED', 'SHIPPED'].includes(order.status)
    );

    return (
        <section className={`page-content ${isCollapsed === null ? '' : isCollapsed ? 'page-content_collapsed' : 'page-content_full-width'}`}>
            {selectedOrder ? (
                <OrderDetail 
                    order={selectedOrder} 
                    onBack={handleBackToOrders}
                    onSendInvoice={selectedOrder && selectedOrder.isNew ? undefined : handleSendInvoice}
                    onPrintInvoice={handlePrintInvoice}
                    isNew={Boolean(selectedOrder && selectedOrder.isNew)}
                    customers={customers}
                    onOrderCreated={handleOrderCreated}
                    onOrderUpdated={handleOrderUpdated}
                    onValidUntilSaved={handleValidUntilSaved}
                    onInvoiceFormSaved={handleInvoiceFormSaved}
                />
            ) : (
                <OrdersTable 
                    ordersList={activeOrders}
                    onOrderClick={handleOrderClick}
                    onApproveInvoice={handleApproveInvoice}
                    approveLoading={approveLoading}
                    pendingApprove={pendingApprove}
                    onCreateOrder={handleCreateOrder}
                    onDeleteOrder={handleDeleteOrder}
                />
            )}
        </section>
    );
};

export default OrdersTab;